import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { findActiveNavItem, navigationGroups, type NavigationItem } from '../../lib/navigation';
import { BRAND } from '../../lib/brand';
import { useAlerts } from '../../contexts/AlertsContext';
import { cn } from '../../lib/utils';

/** Знак бренда: жёлтая метка с дорогой. Отверстие прозрачное, дорога — чёрная в светлой теме, цвет фона в тёмной. */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg className={cn('size-9 shrink-0', className)} viewBox="60 10 392 500" aria-hidden="true">
      <path
        fill="#FFD43B"
        fillRule="evenodd"
        d="M256 486C214 410 96 312 96 186A160 160 0 1 1 416 186C416 312 298 410 256 486ZM256 116A70 70 0 1 0 256 256A70 70 0 1 0 256 116Z"
      />
      <path className="fill-text dark:fill-bg" d="M243 268h26l74 212a14 14 0 0 1-13 18H182a14 14 0 0 1-13-18z" />
      <path fill="#FFD43B" d="M252 290h8l2 34h-12zM249 342h14l3 46h-20zM245 408h22l4 66h-30z" />
    </svg>
  );
}

/** Название с жёлтым «24», как в логотипе. */
export function BrandName() {
  const [word, number] = BRAND.name.split(' ');
  return (
    <>
      {word} <span className="text-accent">{number}</span>
    </>
  );
}

/** Логотип + «Админ-панель» (сайдбар, логин). */
export function BrandLockup({ className }: { className?: string }) {
  return (
    <Link to="/" className={cn('flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}>
      <BrandMark />
      <span className="flex flex-col leading-tight">
        <b className="font-display text-base text-text"><BrandName /></b>
        <span className="text-xs text-text-2">Админ-панель</span>
      </span>
    </Link>
  );
}

interface SidebarProps {
  /** Вызывается после перехода по пункту меню (мобильное меню закрывается). */
  onNavigate?: () => void;
  className?: string;
}

export default function Sidebar({ onNavigate, className }: SidebarProps) {
  const location = useLocation();
  const active = findActiveNavItem(location.pathname);
  const { suspectedDuplicates } = useAlerts();
  const activeLinkRef = useRef<HTMLAnchorElement>(null);

  // Меню длиннее экрана: при открытии экрана снизу списка активный пункт должен быть виден.
  useEffect(() => {
    activeLinkRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active?.id]);

  const badgeFor = (item: NavigationItem): number | null =>
    item.badge === 'suspectedDuplicates' && suspectedDuplicates ? suspectedDuplicates : null;

  return (
    <aside
      aria-label="Разделы"
      className={cn(
        'flex h-full w-64 flex-col border-r border-border-soft bg-surface',
        className,
      )}
    >
      <BrandLockup className="mx-3.5 mb-3 mt-5 shrink-0 px-2" />
      <nav className="flex min-h-0 flex-1 flex-col gap-3 scroll-py-3 overflow-y-auto px-3.5 pb-5 [scrollbar-width:thin]">
        {navigationGroups.map((group) => (
          <div key={group.id} className={cn('flex flex-col gap-0.5', group.label && 'border-t border-border pt-3')}>
            {group.label && (
              <span className="px-2.5 pb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-text-2">
                {group.label}
              </span>
            )}
            {group.items.map((item) => {
              const isActive = active?.id === item.id;
              const badge = badgeFor(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  ref={isActive ? activeLinkRef : undefined}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex h-9 shrink-0 items-center gap-2.5 rounded-[10px] px-2.5 text-sm text-text outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                    isActive
                      ? 'bg-accent-soft font-semibold shadow-[inset_0_0_0_2px_var(--accent)]'
                      : 'hover:bg-muted',
                  )}
                >
                  <Icon
                    className={cn('size-[18px] shrink-0', isActive ? 'text-accent-text' : 'text-text-2')}
                    strokeWidth={1.8}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {badge !== null && (
                    <span
                      className="grid h-5 min-w-5 place-items-center rounded-full bg-inverse px-1.5 text-[11px] font-bold text-on-inverse tabular-nums"
                      aria-label={`ждут решения: ${badge}`}
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
